import { useState } from 'react';
import { Badge } from './ui';
import { RecommendationBadge, type RecommendationLevel } from './StatusBadge';
import { ScreeningAnalysis } from './ScreeningAnalysis';
import { AIScreeningBadge, AIScreeningDetail } from './AIScreeningAnalysis';
import type { Review } from '../types';
import {
  ChevronDown,
  ChevronUp,
  GitCommitHorizontal,
} from 'lucide-react';

interface ReviewVersionHistoryProps {
  reviews: Review[];
  /** Screening answers from the application, passed through for analysis display */
  screeningAnswers?: Record<string, string> | null;
}

const recommendationLabels: Record<number, string> = {
  1: 'Not Recommended',
  2: 'Weak',
  3: 'Neutral',
  4: 'Good',
  5: 'Strongly Recommended',
};

const getScoreColor = (rawScore: number): string => {
  const score100 = rawScore * 2;
  if (score100 >= 80) return 'text-green-600 dark:text-green-400';
  if (score100 >= 60) return 'text-blue-600 dark:text-blue-400';
  if (score100 >= 40) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
};

const getScoreBgColor = (rawScore: number): string => {
  const score100 = rawScore * 2;
  if (score100 >= 80) return 'bg-green-100 dark:bg-green-900/30';
  if (score100 >= 60) return 'bg-blue-100 dark:bg-blue-900/30';
  if (score100 >= 40) return 'bg-amber-100 dark:bg-amber-900/30';
  return 'bg-red-100 dark:bg-red-900/30';
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const parseList = (data: string | null | undefined): string[] => {
  if (!data) return [];
  try {
    const parsed = JSON.parse(data);
    return Array.isArray(parsed) ? parsed : [data];
  } catch {
    return data.split(',').map(s => s.trim()).filter(Boolean);
  }
};

export function ReviewVersionHistory({ reviews, screeningAnswers }: ReviewVersionHistoryProps) {
  const [expandedVersionId, setExpandedVersionId] = useState<number | null>(null);

  // Sort by version ascending so timeline reads top (oldest) to bottom (newest)
  const sorted = [...reviews].sort((a, b) => (a.version ?? 1) - (b.version ?? 1));

  const toggleExpand = (id: number) => {
    setExpandedVersionId(prev => (prev === id ? null : id));
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
        Review History ({sorted.length} version{sorted.length !== 1 ? 's' : ''})
      </h2>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {sorted.map((review, index) => {
          const isActive = !review.archivedAt;
          const isExpanded = expandedVersionId === review.id;
          const prevReview = index > 0 ? sorted[index - 1] : null;
          const isLast = index === sorted.length - 1;

          // Compute score delta from previous version
          const scoreDelta = prevReview
            ? review.overallScore - prevReview.overallScore
            : null;

          // Compute recommendation change
          const recChanged = prevReview && review.recommendation !== prevReview.recommendation;

          return (
            <div key={review.id} className="relative">
              {/* Separator between versions */}
              {index > 0 && (
                <div className="border-t border-gray-100 dark:border-gray-700" />
              )}

              <div className="flex">
                {/* Timeline column */}
                <div className="flex flex-col items-center w-12 flex-shrink-0 py-4">
                  {/* Connecting line above */}
                  {index > 0 && (
                    <div className="w-0.5 h-3 bg-gray-200 dark:bg-gray-600" />
                  )}

                  {/* Node */}
                  <div
                    className={`
                      w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border-2
                      ${isActive
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                        : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
                      }
                    `}
                  >
                    <GitCommitHorizontal
                      className={`h-3.5 w-3.5 ${
                        isActive
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-400 dark:text-gray-500'
                      }`}
                    />
                  </div>

                  {/* Connecting line below */}
                  {!isLast && (
                    <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-600 mt-0" />
                  )}
                </div>

                {/* Content column */}
                <div className="flex-1 min-w-0 py-4 pr-4">
                  {/* Clickable header */}
                  <button
                    onClick={() => toggleExpand(review.id)}
                    className="w-full text-left group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {/* Version badge + status */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            v{review.version ?? 1}
                          </span>

                          {isActive ? (
                            <Badge variant="success" size="xs">Current</Badge>
                          ) : (
                            <Badge variant="neutral" size="xs">Superseded</Badge>
                          )}

                          <RecommendationBadge
                            level={review.recommendation as RecommendationLevel}
                            size="sm"
                          />

                          <AIScreeningBadge rawResponse={review.rawResponse} />
                        </div>

                        {/* Date and score */}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                          <span>{formatDate(review.createdAt)}</span>

                          <span className={`font-medium ${getScoreColor(review.overallScore)}`}>
                            Score: {(review.overallScore / 10).toFixed(1)}/5.0
                          </span>

                          {/* Score change from previous version */}
                          {scoreDelta !== null && scoreDelta !== 0 && (
                            <span
                              className={`font-medium ${
                                scoreDelta > 0
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}
                            >
                              ({scoreDelta > 0 ? '+' : ''}{(scoreDelta / 10).toFixed(1)})
                            </span>
                          )}

                          {/* Recommendation change */}
                          {recChanged && prevReview && (
                            <span className="text-gray-500 dark:text-gray-400">
                              {recommendationLabels[prevReview.recommendation] || prevReview.recommendation}
                              {' -> '}
                              {recommendationLabels[review.recommendation] || review.recommendation}
                            </span>
                          )}
                        </div>

                        {/* Reprocess reason */}
                        {review.reprocessReason && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                            {review.reprocessReason.replace(/_/g, ' ')}
                          </p>
                        )}
                      </div>

                      {/* Score circle + expand icon */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div
                          className={`h-10 w-10 rounded-full flex flex-col items-center justify-center ${getScoreBgColor(review.overallScore)}`}
                        >
                          <span className={`text-sm font-bold ${getScoreColor(review.overallScore)}`}>
                            {(review.overallScore / 10).toFixed(1)}
                          </span>
                        </div>

                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="mt-3 space-y-3">
                      {/* Summary */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Summary
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {review.summary}
                        </p>
                      </div>

                      {/* Strengths */}
                      {parseList(review.strengths).length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-green-700 dark:text-green-400 mb-1 flex items-center gap-1">
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Strengths
                          </h4>
                          <ul className="space-y-1">
                            {parseList(review.strengths).map((s, i) => (
                              <li
                                key={i}
                                className="text-sm text-gray-600 dark:text-gray-400 pl-5 relative before:content-[''] before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-green-400"
                              >
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* AI Screening Analysis from pipeline (US-110) */}
                      <AIScreeningDetail rawResponse={review.rawResponse} />

                      {/* Heuristic Screening Analysis - between strengths and concerns */}
                      {isActive && (
                        <ScreeningAnalysis
                          screeningAnswers={screeningAnswers ?? null}
                          reviewConcerns={parseList(review.concerns)}
                        />
                      )}

                      {/* Concerns */}
                      {parseList(review.concerns).length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-1 flex items-center gap-1">
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            Concerns
                          </h4>
                          <ul className="space-y-1">
                            {parseList(review.concerns).map((c, i) => (
                              <li
                                key={i}
                                className="text-sm text-gray-600 dark:text-gray-400 pl-5 relative before:content-[''] before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-amber-400"
                              >
                                {c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Model metadata */}
                      {review.modelUsed && (
                        <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                            <span>Model: {review.modelUsed.split('/').pop()}</span>
                            {review.modelTier && (
                              <Badge variant="neutral" size="xs">{review.modelTier}</Badge>
                            )}
                            {review.processingTimeMs && (
                              <span>{(review.processingTimeMs / 1000).toFixed(1)}s</span>
                            )}
                            {review.costUsd && (
                              <span>${parseFloat(review.costUsd).toFixed(4)}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

ReviewVersionHistory.displayName = 'ReviewVersionHistory';
